import { ChartLevelKind, ChartLevelStatus, rawChartLevels } from './chart-levels';
import {
    CHART_VIEW,
    CHART_VOLUME_HEIGHT,
    chartPriceTicks,
    chartPriceY,
    chartRange,
    chartVolumeScale,
    stockTickSize
} from './chart-scale';

export { CHART_VIEW, stockTickSize } from './chart-scale';

export interface MarketBar {
    key: string;
    at: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export interface ChartCandle extends MarketBar {
    x: number;
    width: number;
    closeY: number;
    wickTop: number;
    wickBottom: number;
    bodyY: number;
    bodyHeight: number;
    volumeY: number;
    volumeHeight: number;
    volumeCapped: boolean;
    up: boolean;
}

export interface ChartLevel {
    key: string;
    kind: ChartLevelKind;
    label: string;
    price: number;
    stage: number;
    status: ChartLevelStatus;
    plotted: boolean;
    direction: 'above' | 'below' | '';
    y: number;
    tagY: number;
}

export interface PriceTick { key: string; price: number; y: number; }

export interface ChartModel {
    bars: MarketBar[];
    candles: ChartCandle[];
    latest: MarketBar | null;
    levels: ChartLevel[];
    plottedLevels: ChartLevel[];
    priceTicks: PriceTick[];
    timeBars: MarketBar[];
    currentPrice: number;
    currentPriceY: number;
    currentPricePlotted: boolean;
    currentPriceRealtime: boolean;
    previousTradingDate: boolean;
}

const LEFT = CHART_VIEW.left;
const RIGHT = CHART_VIEW.right;
const PRICE_TOP = CHART_VIEW.priceTop;
const PRICE_BOTTOM = CHART_VIEW.priceBottom;
const VOLUME_BOTTOM = CHART_VIEW.volumeBottom;

function number(value: any) { const parsed = Number(value || 0); return Number.isFinite(parsed) ? parsed : 0; }

function barsFrom(source: any[]): MarketBar[] {
    const rows = Array.isArray(source) ? source : [];
    const timestamps = new Set<string>();
    return rows.reduce((valid: MarketBar[], raw: any) => {
        const open = number(raw?.open);
        const high = number(raw?.high);
        const low = number(raw?.low);
        const close = number(raw?.close);
        if (
            Math.min(open, high, low, close) <= 0
            || high < Math.max(open, close, low)
            || low > Math.min(open, close, high)
        ) return valid;
        const at = String(raw?.at || '').trim();
        if (!at || timestamps.has(at)) return valid;
        timestamps.add(at);
        valid.push({
            key: at,
            at,
            open,
            high,
            low,
            close,
            volume: Math.max(0, number(raw?.volume))
        });
        return valid;
    }, []);
}

export function chartBarLimit(viewportWidth: number) {
    if (Number.isFinite(viewportWidth) && viewportWidth > 0) {
        if (viewportWidth <= 560) return 32;
        if (viewportWidth <= 900) return 44;
    }
    return 60;
}

function direction(price: number, range: { min: number; max: number }) {
    if (price > range.max) return 'above' as const;
    if (price < range.min) return 'below' as const;
    return '';
}

function dateKey(value: any) { return String(value || '').slice(0, 10); }

function timeBarsFor(bars: MarketBar[]) {
    if (!bars.length) return [];
    const indexes = bars.length > 2
        ? [0, Math.floor((bars.length - 1) / 2), bars.length - 1]
        : [0, bars.length - 1];
    const seen = new Set<string>();
    return indexes.map(index => bars[index]).filter(bar => {
        if (!bar || seen.has(bar.key)) return false;
        seen.add(bar.key);
        return true;
    });
}

function plottedLevelsFor(levels: ChartLevel[]) {
    const groups = new Map<number, ChartLevel[]>();
    levels.filter(level => level.plotted).forEach(level => {
        groups.set(level.price, [...(groups.get(level.price) || []), level]);
    });
    return Array.from(groups.values()).map(group => {
        if (group.length === 1) return group[0];
        const representative = group.find(level => level.kind === 'order')
            || group.find(level => level.kind === 'average')
            || group[0];
        return {
            ...representative,
            key: `plot:${group.map(level => level.key).join('|')}`,
            label: group.map(level => level.label).join(' · '),
            status: group.find(level => level.status === 'active')?.status
                || group.find(level => level.status === 'next')?.status
                || representative.status
        };
    });
}

function positionedLevelTags(levels: ChartLevel[]) {
    const sorted = [...levels].sort((left, right) => left.y - right.y);
    if (!sorted.length) return sorted;
    const top = PRICE_TOP + 7;
    const bottom = PRICE_BOTTOM - 7;
    const gap = Math.min(21, (bottom - top) / Math.max(1, sorted.length - 1));
    sorted.forEach((level, index) => {
        level.tagY = Math.max(level.y, index ? sorted[index - 1].tagY + gap : top);
    });
    const overflow = sorted[sorted.length - 1].tagY - bottom;
    if (overflow > 0) {
        sorted.forEach(level => { level.tagY -= overflow; });
    }
    return sorted;
}

export function buildChartModel(
    source: any[], cycle: any, order: any, market: any, strategy: any = null
): ChartModel {
    const bars = barsFrom(source);
    const latest = bars.length ? bars[bars.length - 1] : null;
    const tradingDate = dateKey(market?.trading_date);
    const priceBusinessDate = dateKey(market?.price_business_date);
    const realtimeRequested = market?.price_source === 'REALTIME';
    const realtimeDateMatched = realtimeRequested
        && !!tradingDate
        && tradingDate === priceBusinessDate;
    const previousTradingDate = realtimeRequested
        && !!tradingDate
        && !realtimeDateMatched;
    const currentPrice = realtimeDateMatched
        ? number(market?.price)
        : number(latest?.close);
    const rawLevels = rawChartLevels(cycle, order, market, strategy);
    const range = chartRange(
        bars, currentPrice, rawLevels.map(level => level.price),
        rawLevels.filter(level => level.kind !== 'reference')
            .map(level => level.price)
    );
    const step = bars.length ? (RIGHT - LEFT) / bars.length : 0;
    const width = Math.max(4, Math.min(13, step * .72));
    const volumeScale = chartVolumeScale(bars);
    const candles = bars.map((bar, index): ChartCandle => {
        const rawTop = chartPriceY(Math.max(bar.open, bar.close), range);
        const rawBottom = chartPriceY(Math.min(bar.open, bar.close), range);
        const rawHeight = Math.max(0, rawBottom - rawTop);
        const bodyHeight = Math.max(2.8, rawHeight);
        const volumeHeight = bar.volume > 0
            ? Math.max(
                2,
                Math.min(
                    CHART_VOLUME_HEIGHT,
                    bar.volume * CHART_VOLUME_HEIGHT / volumeScale
                )
            )
            : 0;
        return {
            ...bar,
            x: Number((LEFT + (index + .5) * step).toFixed(2)),
            width: Number(width.toFixed(2)),
            closeY: chartPriceY(bar.close, range),
            wickTop: chartPriceY(bar.high, range),
            wickBottom: chartPriceY(bar.low, range),
            bodyY: Number((rawTop - Math.max(0, bodyHeight - rawHeight) / 2).toFixed(2)),
            bodyHeight: Number(bodyHeight.toFixed(2)),
            volumeY: Number((VOLUME_BOTTOM - volumeHeight).toFixed(2)),
            volumeHeight: Number(volumeHeight.toFixed(2)),
            volumeCapped: bar.volume > volumeScale,
            up: bar.close >= bar.open
        };
    });

    const levels = rawLevels.map(level => {
        const outside = direction(level.price, range);
        return {
            ...level,
            plotted: !!bars.length && !outside,
            direction: outside,
            y: outside ? 0 : chartPriceY(level.price, range),
            tagY: outside ? 0 : chartPriceY(level.price, range)
        } as ChartLevel;
    });
    const priceTicks = chartPriceTicks(range);
    return {
        bars,
        candles,
        latest,
        levels,
        plottedLevels: positionedLevelTags(plottedLevelsFor(levels)),
        priceTicks,
        timeBars: timeBarsFor(bars),
        currentPrice,
        currentPriceY: currentPrice > 0 ? chartPriceY(currentPrice, range) : 0,
        currentPricePlotted: !!bars.length && currentPrice > 0,
        currentPriceRealtime: realtimeDateMatched,
        previousTradingDate
    };
}
